/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col, Button } from "@duxton-web/ui"
import { AddCircleFilled } from "@duxton-web/icons"
import { debounce } from "lodash"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { ItemSearchBar } from "components/search/ItemSearchBar"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { Item } from "types/models/menu"
import { ITEM_SEARCH_LIMIT } from "components/search/searchBarConstants"
import {
  CategoryItemDropdown,
  SectionDropdown,
  SellingTimeDropdown,
  ItemPhotoSelect,
} from "components/dropdowns"
import {
  allSellingTime,
  getIsAllSellingTime,
  getSellingTimeByID,
} from "utils/selling_time_helper"
import { headerActions } from "styles/common"
import {
  EventAction,
  EventCategory,
  SearchItemTrigger,
} from "constants/tracking"
import { isItemWithoutPhotoMode } from "utils/menu_helper"
import { performMenuItemSearch } from "utils/rtk_migration_helper"
import LazyLoadWrapper from "components/LazyLoadWrapper"
import ItemPhotoCoverageBanner from "components/banners/ItemPhotoCoverageBanner"
import { TroyWebTracker } from "utils/tracking"
import { useMenuItemSearch } from "hooks/useMenuItemSearch"
import { useSellingTimes } from "hooks/useSellingTimes"
import { actions } from "store/duck/menu"
import { useLazySearchItemsQuery } from "queries/menuApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { useMenuEditorStatus } from "../hooks/useMenuEditorStatus"
import { useItemsWithoutPhotoCount } from "../hooks/useItemsWithoutPhotoCount"

interface IProps {}

const trackItemPhotoCoverageBanner = (
  action: EventAction,
  itemsWithoutPhotoCount: number,
) => {
  TroyWebTracker.sendEvent({
    category: EventCategory.menuOverviewTab,
    action,
    labels: {
      AR_LOCATION_STATENAME: EventCategory.menuOverviewTab,
      NUDGE_ID: "MISSING_PHOTO_NUDGE",
      VIEW_ID: "MISSING_PHOTO_NUDGE",
      PRIORITY_IN_GROUP: "",
      SUMMARY_ORDER: "",
      TITLE_TRANSLATION_KEY:
        itemsWithoutPhotoCount > 1
          ? "mex-insights-menu-card-heading-items-with-no-photo-plural"
          : "mex-insights-menu-card-heading-items-with-no-photo-singular",
      METRIC: "photo_coverage",
    },
  })
}

export const MenuOverviewHeader: FC<IProps> = () => {
  const dispatch = useDispatch()
  const { sellingTimes } = useSellingTimes()

  const {
    menu: { sections },
    isFetchingMenu,
    isMartDepartmentStructure,
    isSellingTimeStructure,
    overview: { sellingTime, search: searchValue },
    itemFilter,
    isItemPhotoCoverageBannerSkipped,
    merchantID,
    merchantGroupID,
  } = useSelector((state: AppState) => state?.menu)

  const { isEditAllowed, isCreateItemAllowed } = useMenuEditorStatus()
  const {
    flags: { searchItems: isRtkSearchItemsEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [searchItemsRtk] = useLazySearchItemsQuery()

  useEffect(() => {
    if (isSellingTimeStructure && sellingTimes) {
      // reselect selling time if selling times data change
      dispatch(
        actionCreator.menu.selectSellingTime(
          getSellingTimeByID(sellingTime?.sellingTimeID, sellingTimes),
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSellingTimeStructure, sellingTimes])

  const hasSections = sections && sections.length > 0

  const handleSelect = (item: Item) => {
    dispatch(actionCreator.menu.selectItemByID(item.itemID ?? ""))

    dispatch(actionCreator.menu.filterItemID(item.itemID))
  }

  const { isSearchEnabled } = useMenuItemSearch()

  const handleSearchChange = debounce(async (value: string) => {
    dispatch(actionCreator.menu.setSearchText(value))
    // Validate search length limit - don't call API if exceeds
    if (value.length > ITEM_SEARCH_LIMIT) {
      return
    }

    if (isSearchEnabled && value.trim().length > 0) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.searchItem,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : { MERCHANT_GROUP_ID: merchantGroupID }),
          SEARCH_TEXT: value.trim(),
          TRIGGER: SearchItemTrigger.searchInput,
        },
      })

      await performMenuItemSearch(value.trim(), {
        showLoading: true,
        searchItemsBaseAction: actions.SEARCH_ITEMS,
        dispatch,
        merchantID,
        merchantGroupID,
        isRtkFlagLoading,
        isRtkSearchItemsEnabled,
        searchItemsRtk,
        dispatchLegacySearch: () =>
          dispatch(
            actionCreator.menu.searchItems({
              merchantID,
              merchantGroupID,
              searchKey: value.trim(),
            }),
          ),
      })
    }
  }, DEFAULT_DEBOUNCE_WAIT_TIME)

  const handleSearchClear = () => {
    dispatch(actionCreator.menu.filterItemID(null))
    dispatch(actionCreator.menu.setSearchText(""))

    // Clear search results if feature flag is enabled
    if (isSearchEnabled) {
      dispatch(actionCreator.menu.clearSearchResults())
    }
  }

  const textCreateNewItem = useTranslation("menu.items.buttons.createNewItem")

  const emptyPhotoImage = require("../../../../assets/images/menu/banner_no_photo.svg")

  // The banner is the only consumer of this count, so do not pay for the
  // catalogue fetch when it cannot render anyway - already dismissed, or the
  // filter the banner leads to is already on.
  const itemsWithoutPhotoCount = useItemsWithoutPhotoCount({
    skip:
      isItemPhotoCoverageBannerSkipped || isItemWithoutPhotoMode(itemFilter),
  })

  const showItemPhotoCoverageBanner = useMemo(() => {
    return (
      itemsWithoutPhotoCount > 0 &&
      !isItemWithoutPhotoMode(itemFilter) &&
      !isItemPhotoCoverageBannerSkipped
    )
  }, [itemsWithoutPhotoCount, itemFilter, isItemPhotoCoverageBannerSkipped])

  const onClickItemPhotoCoverageBannerButton = () => {
    dispatch(actionCreator.menu.itemFilter("itemsWithoutPhoto"))
    trackBannerClick()
  }

  const trackBannerClick = useCallback(() => {
    trackItemPhotoCoverageBanner(EventAction.clickAR, itemsWithoutPhotoCount)
  }, [itemsWithoutPhotoCount])

  const trackBannerSkip = useCallback(() => {
    trackItemPhotoCoverageBanner(EventAction.skipAR, itemsWithoutPhotoCount)
  }, [itemsWithoutPhotoCount])

  const trackBannerView = useCallback(() => {
    trackItemPhotoCoverageBanner(EventAction.default, itemsWithoutPhotoCount)
  }, [itemsWithoutPhotoCount])

  const textMap = useTranslationsMap({
    itemPhotoCoverageBannerTitle:
      itemsWithoutPhotoCount > 1
        ? "itemPhotoCoverage.bannerTitle.plural"
        : "itemPhotoCoverage.bannerTitle.singular",
    itemPhotoCoverageBannerContent:
      itemsWithoutPhotoCount > 1
        ? "itemPhotoCoverage.bannerContent.plural"
        : "itemPhotoCoverage.bannerContent.singular",
    buttonReviewItem:
      itemsWithoutPhotoCount > 1
        ? "itemPhotoCoverage.button.review.item.plural"
        : "itemPhotoCoverage.button.review.item.singular",
    characterExceed: "common.characterExceed",
  })

  const searchError =
    (searchValue || "").length > ITEM_SEARCH_LIMIT
      ? textMap.characterExceed
          .replace("<current>", `${(searchValue || "").length}`)
          .replace("<limit>", `${ITEM_SEARCH_LIMIT}`)
      : ""

  return (
    <Fragment>
      {showItemPhotoCoverageBanner && (
        <Row style={{ margin: "12px 4px 24px 4px" }}>
          <Col span={24}>
            <LazyLoadWrapper>
              <ItemPhotoCoverageBanner
                image={emptyPhotoImage}
                title={textMap.itemPhotoCoverageBannerTitle.replace(
                  "{x}",
                  itemsWithoutPhotoCount.toString(),
                )}
                content={textMap.itemPhotoCoverageBannerContent}
                buttonTitle={textMap.buttonReviewItem}
                onClickButton={onClickItemPhotoCoverageBannerButton}
                trackPageView={trackBannerView}
                onClickSkip={() => {
                  dispatch(
                    actionCreator.menu.toggleItemPhotoCoverageBanner(true),
                  )
                  trackBannerSkip()
                }}
              />
            </LazyLoadWrapper>
          </Col>
        </Row>
      )}
      <Row gutter={12} justify="start">
        <Col span={18}>
          <div css={headerActions}>
            {!isMartDepartmentStructure ? (
              <div
                css={css`
                  min-width: 120px;
                `}
              >
                <CategoryItemDropdown
                  disabled={
                    !isEditAllowed || (!isSellingTimeStructure && !hasSections)
                  }
                />
              </div>
            ) : (
              <Button
                data-testid="create-item-button"
                icon={<AddCircleFilled />}
                onClick={() =>
                  dispatch(
                    actionCreator.menu.toggleDrawer({
                      item: null,
                      showItem: true,
                    }),
                  )
                }
                disabled={!isCreateItemAllowed}
              >
                {textCreateNewItem}
              </Button>
            )}

            {isSellingTimeStructure ? (
              <div
                css={css`
                  min-width: 220px;
                `}
              >
                <SellingTimeDropdown
                  disabled={!isEditAllowed}
                  isEditable={isEditAllowed}
                  selectedSellingTime={sellingTime || allSellingTime}
                  sellingTimes={[allSellingTime, ...sellingTimes]}
                  onChange={selectedSellingTime => {
                    const isAllSellingTime = getIsAllSellingTime(
                      selectedSellingTime,
                    )
                    dispatch(
                      actionCreator.menu.selectSellingTime(
                        !isAllSellingTime ? selectedSellingTime : undefined,
                      ),
                    )
                  }}
                  eventTrackerObject={{
                    eventCategoryTracker: EventCategory.menuOverviewTab,
                  }}
                />
              </div>
            ) : (
              !isMartDepartmentStructure &&
              (hasSections || isFetchingMenu) && (
                <div
                  css={css`
                    flex: 1 1;
                    min-width: 0;
                  `}
                >
                  <SectionDropdown disabled={isFetchingMenu} />
                </div>
              )
            )}
            <ItemPhotoSelect />
          </div>
        </Col>
        <Col span={6}>
          <ItemSearchBar
            data-testid="item-search-bar"
            onSelect={handleSelect}
            onSearch={handleSearchChange}
            onClear={handleSearchClear}
            hideDropdownResults={isSearchEnabled}
          />
          {searchError && (
            <div
              css={css`
                color: #d63031;
                font-size: 12px;
                margin-top: 4px;
              `}
            >
              {searchError}
            </div>
          )}
        </Col>
      </Row>
    </Fragment>
  )
}
